import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { LangToggle } from "@/components/LangToggle";
import { Button } from "@/components/ui/button";
import { API } from "@/lib/api";
import axios from "axios";
import {
  Building2, BarChart3, Users, Languages, Lock, Megaphone, Mail,
  ArrowRight, CheckCircle2, Loader2, Send,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

/** Thin hook: fetches super-admin overrides for landing copy and exposes a
 *  `tt(key, fallback)` resolver so each render falls back to the i18n default
 *  when the admin hasn't overridden that key. */
function useLandingOverrides(lang) {
  const [overrides, setOverrides] = useState({ en: {}, th: {} });
  useEffect(() => {
    axios.get(`${API}/landing-content`).then(r => setOverrides(r.data || {})).catch(() => {});
  }, []);
  const map = overrides?.[lang] || {};
  return (key, fallback) => map[key] || fallback;
}

function Benefit({ icon: Icon, title, body, testId }) {
  return (
    <div
      className="relative rounded-xl border border-stone-200/80 bg-white/70 p-6 backdrop-blur-sm hover:bg-white transition shadow-sm"
      data-testid={testId}
    >
      <div className="w-11 h-11 rounded-lg bg-emerald-900/5 text-emerald-800 flex items-center justify-center mb-4">
        <Icon className="w-5 h-5" />
      </div>
      <h3 className="font-serif text-lg text-stone-900 mb-1.5">{title}</h3>
      <p className="text-sm text-stone-600 leading-relaxed">{body}</p>
    </div>
  );
}

function EnquiryForm({ t, kind, variant, extraPayload = {} }) {
  // Fields differ between the two enquiry sections. Agent form captures a
  // region; shop form captures the shop name and zip code. Rendering stays
  // consistent so maintenance stays cheap.
  const isShop = kind === "shop";
  const [name, setName] = useState("");
  const [region, setRegion] = useState("");
  const [shopName, setShopName] = useState("");
  const [zip, setZip] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [renderTs] = useState(() => Date.now());
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  // Variant lets the pricing banner use a light-on-dark emerald look while
  // the agents section keeps the amber-glow dark variant.
  const dark = variant !== "emerald";
  const inputClass = dark
    ? "bg-white/10 border-amber-200/20 text-amber-50 placeholder:text-amber-100/50 focus-visible:ring-amber-400"
    : "bg-white/10 border-amber-200/30 text-amber-50 placeholder:text-amber-100/60 focus-visible:ring-amber-300";

  const submit = async (e) => {
    e.preventDefault();
    const required = isShop
      ? name.trim() && (shopName.trim() || zip.trim()) && phone.trim()
      : name.trim() && region.trim() && phone.trim();
    if (!required) {
      toast.error(t(isShop ? "public.enquiry.required_shop" : "public.enquiry.required"));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/landing/enquiry`, {
        name: name.trim(),
        phone: phone.trim(),
        region: isShop ? null : region.trim(),
        shop_name: isShop ? (shopName.trim() || null) : null,
        zip_code: isShop ? (zip.trim() || null) : null,
        enquiry_type: isShop ? "shop" : "agent",
        message: message.trim() || null,
        website: website || null,
        render_ts: renderTs,
        ...extraPayload,
      });
      setSent(true);
      toast.success(t("public.enquiry.success"));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || t("public.enquiry.error"));
    } finally { setSubmitting(false); }
  };

  if (sent) {
    return (
      <div
        className="rounded-xl bg-emerald-800/30 border border-emerald-300/30 p-5 text-left"
        data-testid={isShop ? "shop-enquiry-sent" : "enquiry-sent"}
      >
        <CheckCircle2 className="w-6 h-6 text-emerald-300 mb-2" />
        <div className="font-serif text-xl text-amber-50">{t("public.enquiry.thanks_title")}</div>
        <p className="text-sm text-amber-100/80 mt-1 leading-relaxed">{t("public.enquiry.thanks_body")}</p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="space-y-2.5 text-left"
      data-testid={isShop ? "shop-enquiry-form" : "agents-enquiry-form"}
    >
      {/* Honeypot — kept off-screen and off tab-order. */}
      <div aria-hidden style={{ position: "absolute", left: "-9999px", width: 1, height: 1, overflow: "hidden" }}>
        <label>
          Website
          <input type="text" name="website" tabIndex={-1} autoComplete="off"
            value={website} onChange={(e) => setWebsite(e.target.value)} />
        </label>
      </div>
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={t("public.enquiry.name_placeholder")}
        className={inputClass}
        maxLength={120} required
        data-testid={isShop ? "shop-enquiry-name" : "enquiry-name"}
      />
      {isShop ? (
        <>
          <Input
            value={shopName}
            onChange={(e) => setShopName(e.target.value)}
            placeholder={t("public.enquiry.shop_name_placeholder")}
            className={inputClass}
            maxLength={200} required
            data-testid="shop-enquiry-shop-name"
          />
          <Input
            value={zip}
            onChange={(e) => setZip(e.target.value)}
            placeholder={t("public.enquiry.zip_placeholder")}
            className={inputClass}
            maxLength={20} required
            data-testid="shop-enquiry-zip"
          />
        </>
      ) : (
        <Input
          value={region}
          onChange={(e) => setRegion(e.target.value)}
          placeholder={t("public.enquiry.region_placeholder")}
          className={inputClass}
          maxLength={120} required
          data-testid="enquiry-region"
        />
      )}
      <Input
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        placeholder={t("public.enquiry.phone_placeholder")}
        inputMode="tel"
        className={inputClass}
        maxLength={30} required
        data-testid={isShop ? "shop-enquiry-phone" : "enquiry-phone"}
      />
      <Textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder={t("public.enquiry.message_placeholder")}
        rows={3} maxLength={1000}
        className={`${inputClass} resize-none`}
        data-testid={isShop ? "shop-enquiry-message" : "enquiry-message"}
      />
      <Button
        type="submit"
        disabled={submitting}
        className="w-full rounded-full bg-amber-400 hover:bg-amber-300 text-stone-900 font-medium"
        data-testid={isShop ? "shop-enquiry-submit" : "enquiry-submit"}
      >
        {submitting ? (
          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("public.enquiry.sending")}</>
        ) : (
          <><Send className="w-4 h-4 mr-2" /> {t("public.enquiry.submit")}</>
        )}
      </Button>
    </form>
  );
}

function AgentEnquiryForm({ t }) {
  return <EnquiryForm t={t} kind="agent" />;
}

export default function PublicLanding() {
  const { t, lang } = useLang();
  const o = useLandingOverrides(lang);
  const tt = (k) => o(k, t(k));
  return (
    <div className="min-h-screen bg-[#faf7f1] text-stone-900 relative overflow-hidden">
      {/* Decorative background wash */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(ellipse at top left, rgba(6,95,70,0.08), transparent 55%), radial-gradient(ellipse at bottom right, rgba(161,98,7,0.08), transparent 55%)",
        }}
      />

      <header className="relative z-10">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="public-logo-link">
            <img src="/corp-logo.png" alt="Relax-MIS" className="h-9 sm:h-10 w-auto" />
          </Link>
          <div className="flex items-center gap-3">
            <LangToggle variant="light" />
            <Button
              asChild
              size="sm"
              variant="outline"
              className="rounded-full border-stone-300 text-stone-700 hover:bg-stone-100"
              data-testid="public-admin-portal-btn"
            >
              <Link to="/admin">
                <Lock className="w-3.5 h-3.5 mr-1.5" />
                {tt("public.admin_portal")}
              </Link>
            </Button>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Hero */}
        <section className="pt-10 sm:pt-20 pb-16 grid lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-3">
            <span className="inline-block caption-label text-emerald-800 bg-emerald-900/5 rounded-full px-3 py-1 mb-5">
              {tt("public.hero.eyebrow")}
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-stone-900 leading-tight tracking-tight">
              {tt("public.hero.title_a")}{" "}
              <span className="text-emerald-800">{tt("public.hero.title_b")}</span>
            </h1>
            <p className="mt-5 text-stone-600 text-base sm:text-lg leading-relaxed max-w-xl">
              {tt("public.hero.subtitle")}
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Button
                asChild
                className="rounded-full bg-emerald-800 hover:bg-emerald-900 text-amber-50 px-6"
                data-testid="public-cta-contact"
              >
                <a href="mailto:admin@relax-mis.com">
                  <Mail className="w-4 h-4 mr-2" /> {tt("public.hero.cta_contact")}
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                className="rounded-full border-stone-300 text-stone-800 hover:bg-white px-6"
                data-testid="public-cta-benefits"
              >
                <a href="#benefits">
                  {tt("public.hero.cta_benefits")} <ArrowRight className="w-4 h-4 ml-2" />
                </a>
              </Button>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-stone-500">
              <span className="inline-flex items-center gap-1.5"><Languages className="w-3.5 h-3.5" /> {tt("public.hero.pill_bilingual")}</span>
              <span className="inline-flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" /> {tt("public.hero.pill_multi_tenant")}</span>
              <span className="inline-flex items-center gap-1.5"><BarChart3 className="w-3.5 h-3.5" /> {tt("public.hero.pill_reports")}</span>
            </div>
          </div>
          <div className="lg:col-span-2 lg:pl-8">
            <div className="relative rounded-2xl bg-gradient-to-br from-emerald-900 via-emerald-800 to-amber-700 p-6 shadow-xl ring-1 ring-emerald-900/40">
              <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-amber-400/20 blur-2xl" aria-hidden />
              <div className="rounded-lg bg-white/95 p-5 shadow-inner">
                <div className="caption-label text-stone-500">{tt("public.sample.heading")}</div>
                <div className="mt-2 flex items-end justify-between">
                  <div>
                    <div className="font-serif text-3xl text-emerald-900 font-semibold">฿128,420</div>
                    <div className="text-xs text-stone-500 mt-1">{tt("public.sample.subtitle")}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-stone-500">{tt("public.sample.customers")}</div>
                    <div className="font-mono text-xl text-stone-800">142</div>
                  </div>
                </div>
                <div className="mt-5 h-20 flex items-end gap-1.5">
                  {[42, 58, 36, 71, 54, 65, 83].map((h, i) => (
                    <div key={i} className="flex-1 rounded-sm bg-emerald-800/80" style={{ height: `${h}%` }} />
                  ))}
                </div>
              </div>
              <div className="mt-4 text-amber-50/90 text-xs font-mono tracking-wide">
                {tt("public.sample.footer")}
              </div>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section id="benefits" className="pt-6 pb-20 scroll-mt-24">
          <div className="mb-10">
            <h2 className="font-serif text-3xl sm:text-4xl text-stone-900">{tt("public.benefits.title")}</h2>
            <p className="mt-2 text-stone-600 max-w-2xl">{tt("public.benefits.subtitle")}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            <Benefit
              icon={Building2}
              title={tt("public.benefit.multi.title")}
              body={tt("public.benefit.multi.body")}
              testId="benefit-multi-tenant"
            />
            <Benefit
              icon={BarChart3}
              title={tt("public.benefit.reports.title")}
              body={tt("public.benefit.reports.body")}
              testId="benefit-reports"
            />
            <Benefit
              icon={Users}
              title={tt("public.benefit.staff.title")}
              body={tt("public.benefit.staff.body")}
              testId="benefit-staff"
            />
          </div>
        </section>

        {/* Pricing banner + shop enquiry form */}
        <section className="pb-10">
          <div
            className="relative overflow-hidden rounded-2xl bg-emerald-900 text-amber-50 px-6 sm:px-10 py-8 sm:py-10 shadow-sm"
            data-testid="pricing-banner"
          >
            <div className="absolute -top-10 -right-10 w-60 h-60 rounded-full bg-amber-500/15 blur-3xl" aria-hidden />
            <div className="relative grid lg:grid-cols-5 gap-8 items-center">
              <div className="lg:col-span-3">
                <p className="font-serif text-2xl sm:text-3xl leading-snug text-amber-50">
                  {tt("public.pricing.banner")}
                </p>
                <p className="mt-3 text-amber-100/85 leading-relaxed max-w-xl">
                  {tt("public.pricing.pitch")}
                </p>
                <ul className="mt-5 space-y-2 text-amber-100/90 text-sm max-w-xl">
                  {[
                    tt("public.pricing.point_1"),
                    tt("public.pricing.point_2"),
                    tt("public.pricing.point_3"),
                  ].map((p, i) => (
                    <li key={i} className="flex gap-2">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-emerald-300" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="lg:col-span-2">
                <div className="caption-label text-amber-300 mb-2">{tt("public.pricing.form_label")}</div>
                <EnquiryForm t={t} kind="shop" variant="emerald" />
              </div>
            </div>
          </div>
        </section>

        {/* Agents wanted */}
        <section id="agents" className="pb-24">
          <div
            className="relative overflow-hidden rounded-2xl bg-stone-900 text-amber-50 px-6 sm:px-10 py-10 sm:py-12"
            data-testid="agents-wanted-section"
          >
            <div className="absolute -top-10 -right-10 w-60 h-60 rounded-full bg-amber-500/15 blur-3xl" aria-hidden />
            <div className="absolute -bottom-16 -left-10 w-72 h-72 rounded-full bg-emerald-500/15 blur-3xl" aria-hidden />
            <div className="relative grid lg:grid-cols-5 gap-8 items-center">
              <div className="lg:col-span-3">
                <div className="inline-flex items-center gap-2 caption-label text-amber-300 bg-amber-500/10 rounded-full px-3 py-1 mb-4">
                  <Megaphone className="w-3.5 h-3.5" /> {tt("public.agents.eyebrow")}
                </div>
                <h2 className="font-serif text-3xl sm:text-4xl">{tt("public.agents.title")}</h2>
                <p className="mt-3 text-amber-100/85 leading-relaxed max-w-xl">
                  {tt("public.agents.body")}
                </p>
                <ul className="mt-5 space-y-2 text-amber-100/90 text-sm">
                  {[
                    tt("public.agents.point_1"),
                    tt("public.agents.point_2"),
                    tt("public.agents.point_3"),
                  ].map((p, i) => (
                    <li key={i} className="flex gap-2">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-emerald-300" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="lg:col-span-2 lg:text-right">
                <div className="caption-label text-amber-300 mb-2">{tt("public.agents.contact_label")}</div>
                <AgentEnquiryForm t={t} />
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="relative z-10 border-t border-stone-200/70 bg-white/50">
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-stone-500">
          <span>© {new Date().getFullYear()} Relax-MIS</span>
          <div className="flex items-center gap-4">
            <a href="mailto:admin@relax-mis.com" className="hover:text-stone-800">admin@relax-mis.com</a>
            <Link to="/admin" className="hover:text-stone-800" data-testid="public-footer-admin-link">
              {tt("public.admin_portal")}
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
